import { uuidSchema } from './common';

// ── Объект затрат заявки (план `docs/route-trips-plan.md`, Р25) ──
//
// Заказчик заявки на технику и сегодня ровно один: объект строительства **или** отдел (ADR 0040),
// и это держит CHECK `vehicle_requests_customer_check` (`num_nonnulls(object_id, department_id) = 1`).
// Двойственность возникает не в базе, а в чтении: два десятка мест выводят заказчика сами, из пары
// `objectName`/`departmentName`, и каждое вольно решить иначе — где-то по имени, где-то по
// идентификатору, где-то с кодом, где-то без. Поэтому один тип и общие для портала и сервера
// функции при нём: вывод из пары, ключ и разбор ключа обратно.
//
// Колонкой объект затрат не хранится намеренно: он выводится из пары, которую уже стережёт CHECK,
// а копия рассинхронизировалась бы при первом же переводе заявки с отдела на объект.
//
// Три границы, чтобы двойственность не вернулась:
//
//   - **площадка отдела объект затрат не подменяет.** У отдела бывает закреплённый объект
//     (`departments.construction_object_id`, ADR 0062), но он про **область видимости**, а не про
//     учёт: он открывает сотрудникам отдела вывоз мусора со своей площадки — и только. Заявку
//     завёл отдел — затраты на отдел, даже если у отдела ровно один объект и «всем очевидно», куда
//     их отнести. Записано явно, потому что спор об этом неизбежен: подставить площадку выглядит
//     полезным ровно до первого отдела с площадкой, чьи расходы бухгалтерия ждёт на отделе;
//   - **ездка своего объекта затрат не имеет** — наследует заявкин (Р25). Иначе двойственность
//     переезжает внутрь заявки;
//   - **точка маршрута тоже**: на ней сходятся ездки разных заказчиков, и «объект затрат точки» не
//     существует. Учёт идёт по строкам задания — ездкам и линейным дням, — а не по остановкам.

/**
 * Объект затрат: на кого относятся расходы по заявке.
 *
 * Дискриминированное объединение, а не «объект с необязательным отделом»: у объекта и отдела
 * общие только подпись и код, а вопросы к ним разные — по объекту спрашивают стройку, по отделу
 * подразделение, — и сведённые в одну плоскую запись они дали бы один ответ на два вопроса.
 * Ветвь читается явно (`t.kind === 'object'`), и ни одно место не забудет, что заказчиков два
 * рода.
 *
 * `code` есть у обоих справочников (`construction_objects.code`, `departments.code` — обе
 * колонки `NOT NULL` и `UNIQUE`), поэтому у объекта затрат он есть всегда. Это то, чем его
 * называет бухгалтерия и чем он печатается в ЭСМ-2 (`object_code`).
 */
export type CostTarget =
  | { kind: 'object'; id: string; code: string; name: string }
  | { kind: 'department'; id: string; code: string; name: string };

/**
 * Ссылка на объект затрат: род и идентификатор — всё, что несёт ключ.
 *
 * Отдельный тип, а не `CostTarget` с необязательными подписью и кодом: их здесь не «опустили за
 * ненадобностью», их **неоткуда взять**. Ключ кода и имени не хранит намеренно (`costTargetKey`), и
 * разбор, дописывающий `code: ''`, выдал бы ровно то выдуманное значение, которого избегает
 * `costTargetOf`: там пустой код означает «колонку не выбрали запросом», а здесь означал бы «код у
 * справочника есть, но мы его потеряли». От полного `CostTarget` такая запись неотличима — и
 * подставилась бы туда, где код печатают (ЭСМ-2, `object_code`).
 *
 * Дискриминированное объединение по той же причине, что и у `CostTarget`: пара
 * `{ objectId, departmentId }` для тела запроса собирается ветвлением по роду, а не двумя полями,
 * одно из которых всегда пусто и непонятно почему (план `docs/department-requests-plan.md`, Р2).
 *
 * Полный `CostTarget` этому типу удовлетворяет структурно — потому `costTargetKey` берёт ссылку и
 * остаётся одной функцией на оба случая.
 */
export type CostTargetRef = { kind: 'object'; id: string } | { kind: 'department'; id: string };

/** Ключ объекта затрат: `object:<uuid>` либо `department:<uuid>` — тип узкий, как у `AddressKey`. */
export type CostTargetKey = `${CostTarget['kind']}:${string}`;

/**
 * Заявка глазами объекта затрат — ровно те шесть колонок, которыми заказчик описан в базе и в
 * DTO (`VehicleRequestBaseDto`).
 *
 * Плоская шестёрка, а не вложенные `object`/`department`: именно так заказчик приходит отовсюду —
 * из DTO заявки, из строки `leftJoin` на оба справочника (потому и `| null` у всех полей: чужая
 * сторона пары пуста по устройству, а не по недосмотру), из снимка листа. Требовать здесь другую
 * форму значило бы заставить два десятка мест собирать её перед вызовом — и объект затрат,
 * который затевался ради одного правила на всех, стало бы некуда подставить.
 */
export interface CostTargetSource {
  objectId: string | null;
  objectCode: string | null;
  objectName: string | null;
  departmentId: string | null;
  departmentCode: string | null;
  departmentName: string | null;
}

/**
 * Объект затрат заявки: ровно один из пары под CHECK.
 *
 * Спрашивается **идентификатор**, а не имя: CHECK стережёт `object_id`/`department_id`, и только
 * они отвечают «чья заявка» однозначно. Имя годится на подпись, но заявка с пустым наименованием
 * справочника — испорченная запись, а не заявка отдела.
 *
 * `null` — заказчика нет ни одного. По базе такого состояния не бывает, но эта функция читает и
 * то, что собрано запросом: строка, где заказчика просто не выбрали в `select`, и заявка,
 * прочитанная до `join`, приходят сюда неотличимо от пустой. Бросать здесь исключение значило бы
 * ронять список заявок из-за подписи одной строки — поэтому «неизвестно» возвращается значением,
 * а показывают его тем же «—», которым это делает `requestCustomerName` сегодня.
 *
 * Площадка отдела (ADR 0062) результат не меняет: заявку завёл отдел — объект затрат отдел.
 */
export function costTargetOf(r: CostTargetSource): CostTarget | null {
  if (r.objectId) {
    // Код в справочнике `NOT NULL`, и `null` здесь означает лишь то, что колонку не выбрали
    // запросом. Пустая строка честнее выдуманного значения: она видна на экране как пропуск.
    return { kind: 'object', id: r.objectId, code: r.objectCode ?? '', name: r.objectName ?? '' };
  }
  if (r.departmentId) {
    return {
      kind: 'department',
      id: r.departmentId,
      code: r.departmentCode ?? '',
      name: r.departmentName ?? '',
    };
  }
  return null;
}

/**
 * Ключ для сравнения и сводов: `object:<uuid>` | `department:<uuid>`.
 *
 * Род в ключе обязателен, хотя uuid и так уникален: ключ не только сравнивается, но и читается —
 * им сгруппированы затраты листа и им же поднимается `multiple_cost_targets` (Р26), — а
 * голый uuid не говорит, чей он. Кода в ключе нет: коды у объектов и отделов свои и совпасть
 * могут, а сущность за ними разная — сведённые по коду затраты стройки и отдела слились бы в
 * одну строку.
 *
 * Спрашивается `CostTargetRef`, а не полный объект затрат: ключу нужны род и идентификатор, а
 * требовать подпись значило бы, что поле формы, у которого на руках один разобранный ключ, собрать
 * его обратно не может. Полный `CostTarget` подходит сюда структурно, поэтому функция осталась
 * одна: второй `costTargetRefKey` рядом дал бы два ответа на один вопрос и разъехался бы с первым
 * на первой же поправке формата.
 */
export function costTargetKey(ref: CostTargetRef): CostTargetKey {
  return `${ref.kind}:${ref.id}`;
}

/**
 * Ключ прямо из шести плоских полей: форме правки собирать его больше не из чего — у неё на руках
 * DTO заявки, а не готовый объект затрат.
 *
 * Через `costTargetOf`, а не вторым разбором пары, хотя разбор этот в две строки: правило «кто
 * заказчик» — одно (ветвление по идентификатору, площадка отдела отдел не подменяет), и копия
 * ответила бы на тот же вопрос второй раз, разойдясь с оригиналом на первой же поправке правила.
 *
 * `null` — там же, где он у `costTargetOf`: заказчика не видно ни одного. Форма показывает на этом
 * пустое поле, а не выдуманный выбор — правку от того, что заказчика не выбрали, отличает сервер.
 */
export function costTargetKeyOf(source: CostTargetSource): CostTargetKey | null {
  const target = costTargetOf(source);
  return target ? costTargetKey(target) : null;
}

/**
 * Разбор ключа обратно в ссылку — единственное место, где `object:<uuid>` превращается в пару
 * `{ objectId, departmentId }` тела запроса (план `docs/department-requests-plan.md`, Р2).
 *
 * Проверяются **обе** половины: род из двух известных и идентификатор как UUID. Одного префикса
 * мало — `object:` с мусором после двоеточия поле формы показало бы принятым, а вернулся бы он
 * четырёхсотым от схемы запроса, когда человек уже ушёл со своего действия.
 *
 * UUID спрашивается той же `uuidSchema`, которой `objectId` и `departmentId` проверяют схемы
 * запросов: два представления о том, что такое идентификатор, разошлись бы — и портал принимал бы
 * ключ, который сервер отвергает. `safeParse` синхронный, так что функция остаётся чистой и годится
 * прямо в рендере поля.
 *
 * Режется по **первому** двоеточию, но остаток проверяется целиком: `object:<uuid>:extra` не
 * пройдёт. Лишний разделитель — не хвост, который можно отбросить, а другой формат; прочитанный
 * молча, он означал бы, что ключ склеен не тем, чем разбирается.
 *
 * `null`, а не исключение: сюда приходит внешнее — значение поля, параметр фильтра из адресной
 * строки, — и «не ключ» здесь обычный ответ, на который у формы есть поведение.
 */
export function parseCostTargetKey(key: string): CostTargetRef | null {
  const separator = key.indexOf(':');
  if (separator < 0) return null;

  const kind = key.slice(0, separator);
  const id = key.slice(separator + 1);
  if (kind !== 'object' && kind !== 'department') return null;

  return uuidSchema.safeParse(id).success ? { kind, id } : null;
}
